import api from "../api"
import type { DeepReadonly } from "vue"
import { useQuery } from "@tanstack/vue-query"
import type { CurrentOrderOrderItemModifier } from "@/composables/useCurrentOrder"
import { queryClient } from "@/queryClient"
import { BASE_PRODUCT_MODIFIERS_QUERY_KEY } from "@/services/queryKeys"

export type ProductModifierOption = DeepReadonly<{
  id: string
  label: string
  priceOffset: number
  isSelected: boolean
}>

export type ProductModifierGroup = DeepReadonly<{
  id: string
  label: string
  isShared: boolean
  minOptions: number
  maxOptions: number

  options: ProductModifierOption[]
}>

export const listProductModifiers = async (): Promise<
  ProductModifierGroup[]
> => {
  const response = await api.get<ProductModifierGroup[]>(
    "/products/product-modifiers/"
  )

  return response.body
}

export const useProductModifers = () => {
  const { data, isFetching } = useQuery({
    queryKey: [BASE_PRODUCT_MODIFIERS_QUERY_KEY],
    queryFn: () => listProductModifiers(),
    staleTime: 60 * 60 * 1000,
    queryClient,
  })

  const getById = (modifierGroupId: string) =>
    computed(() =>
      data.value?.find(
        (modifierGroup) =>
          modifierGroup.id === modifierGroupId
      )
    )

  const getByIds = (modifierGroupIds: string[]) =>
    computed(() =>
      modifierGroupIds.reduce((acc, id) => {
        const group = getById(id)
        return group.value ? [...acc, group.value] : acc
      }, [] as ProductModifierGroup[])
    )

  return {
    data,
    isFetching,
    getById,
    getByIds,
    asFlatList: computed<CurrentOrderOrderItemModifier[]>(
      () =>
        data.value?.flatMap(({ options, id: groupId }) =>
          options.map(({ id, priceOffset, label }) => ({
            id,
            groupId,
            priceOffset,
            label,
          }))
        ) ?? []
    ),
  }
}
